import React, { useReducer, useEffect, useState } from "react";
import { useRouter } from "next/router";
import styled, { css } from "styled-components";
import get from "lodash/get";
import toNumber from "lodash/toNumber";
import { Transition, SwitchTransition } from "react-transition-group";
import wait from "waait";
import generateQuizResults from "lib/quizResults";
import { checkUTMOverride } from "lib/overrides/utmOverride";
import { startQuiz } from "lib/models/QuizTaken";
import QuizProvider from "lib/QuizContext";
import reducer from "./reducer";
import ProgressBar from "./ProgressBar";
import ActionMenu from "./ActionMenu";
import QuizHeader from "./Header";
import Footer from "./Footer";
import Question from "./Question";
import Loader from "./Loader";
// import Survata from "./Survata";
import { getQuestion, getProgress } from "./utils";
import AdSense, { hasAdSense } from "components/AdSense";
import { pickBy, size } from "lodash";

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  min-height: 100vh;
  background: #fff url(/quiz/background.png) center center no-repeat;
  background-size: cover;
`;

const LoaderContainer = styled.div`
  position: fixed;
  z-index: 999;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 2rem;
  background-color: rgba(255, 255, 255, 0.5);

  svg {
    display: block;
    margin: 0 auto;
    width: 100%;
    max-width: 20rem;
  }
`;

const LoaderBanner = styled.div`
  position: fixed;
  z-index: 1000;
  bottom: 30px;
  left: 0;
  display: block;
  width: 100%;
  height: auto;
`;

const Form = styled.form`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
`;

const FadeTransition = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  transition: opacity 0.4s ease;
  opacity: ${(props) => {
    switch (props.state) {
      case "entering":
      case "entered":
        return 1;
      default:
        return 0;
    }
  }};
`;

const Content = styled.main`
  flex: 1 1 auto;
  display: flex;
  padding: 1.5rem;
  align-items: ${(props) =>
    props.adsense && props.mobileRows < 4 ? "flex-start" : "center"};

  @media (min-width: 640px) {
    align-items: ${(props) =>
      props.adsense && props.rows < 3 ? "flex-start" : "center"};
  }
`;

const AdSenseContainer = styled.div`
  ${(props) =>
    props.mobileRows > 3
      ? css`
          visibility: hidden;
          opacity: 0;
          max-height: 0;
        `
      : css`
          visibility: visible;
          opacity: 1;
          max-height: 13rem;
        `}
  overflow: hidden;
  transition: opacity 0.4s linear, visibility 0.4s linear,
    max-height 0s linear 0.4s;

  & > div {
    margin-top: 2rem;
    margin-bottom: 2rem;
  }

  @media (min-width: 640px) {
    ${(props) =>
      props.rows > 2
        ? css`
            visibility: hidden;
            opacity: 0;
            max-height: 0;
          `
        : css`
            visibility: visible;
            opacity: 1;
            max-height: 13rem;
          `}
  }
`;

const Questionnaire = (props) => {
  const router = useRouter();

  const [state, dispatch] = useReducer(reducer, getInitialState(props));

  const [quizTakenId, setQuizTakenId] = useState(null);
  const [loading, setLoading] = useState(false);

  const percent = getProgress(state);

  const currentQuestion = getQuestion(
    state.quizzes,
    state.current.quiz,
    state.current.question
  );

  const showAdSense = hasAdSense({
    location: "questions",
    user: props.user,
    ...props.quiz.adsense,
  });

  // Start Quiz Taken after first answer
  useEffect(() => {
    const beginQuiz = async () => {
      const id = await startQuiz(props.user, props.quiz.id);
      setQuizTakenId(id);
    };

    if (state.answers.length > 0 && !quizTakenId) beginQuiz();
  }, [state.answers.length]);

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();

    if (loading) return;

    await setLoading(true);
    await dispatch({ type: "SUBMIT_QUESTION" });
  };

  const handleNoBudget = async () => {
    if (loading) return;

    await setLoading(true);
    await dispatch({ type: "SUBMIT_QUESTION", skip: true });
  };

  const submitQuiz = async (state) => {
    const tag = await checkUTMOverride(props.quiz, props.country);

    const delay = toNumber(get(props, "resultsDelay", 0));
    await wait(delay);

    const id = await generateQuizResults(quizTakenId, {
      ...state,
      quiz: props.quiz,
      country: props.country,
      tag,
    });

    if (!id) {
      router.push("/quiz/[slug]", `/quiz/${props.quiz.slug}`);
    } else {
      router.push(
        "/quiz/[slug]/result/[id]",
        `/quiz/${props.quiz.slug}/result/${id}`
      );
    }
  };

  const handlePrevious = async () => {
    if (loading) return;

    await setLoading(true);
    await dispatch({ type: "PREVIOUS_QUESTION" });
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    setTimeout(() => {
      setLoading(false);
    }, 300);
  }, [state.current.question]);

  useEffect(() => {
    if (state.completed) submitQuiz(state);
  }, [state.completed]);

  return (
    <QuizProvider country={props.country}>
      <Container>
        <ProgressBar percent={percent} />

        <ActionMenu dispatch={dispatch} slug={props.quiz.slug} />

        {/* <Survata /> */}

        <QuizHeader name={props.quiz.name} />

        {!state.completed && showAdSense && (
          <AdSenseContainer {...getRowCount(currentQuestion)}>
            <AdSense
              location="questions"
              type="banner"
              {...props.quiz.adsense}
              user={props.user}
            />
          </AdSenseContainer>
        )}

        <Form onSubmit={handleSubmitQuestion} noValidate>
          <SwitchTransition>
            <Transition
              key={
                state.completed
                  ? "completed"
                  : `question-${state.current.question}-${state.current.quiz}`
              }
              timeout={400}
            >
              {(transition) => (
                <FadeTransition state={transition} completed={state.completed}>
                  {state.completed ? (
                    <>
                      <LoaderContainer>
                        <Loader />
                      </LoaderContainer>

                      {showAdSense && (
                        <LoaderBanner>
                          <AdSense
                            location="questions"
                            type="banner"
                            user={props.user}
                            {...props.quiz.adsense}
                          />
                        </LoaderBanner>
                      )}
                    </>
                  ) : (
                    <React.Fragment>
                      <Content
                        adsense={showAdSense}
                        {...getRowCount(currentQuestion)}
                      >
                        <Question
                          dispatch={dispatch}
                          answer={state.answer}
                          {...currentQuestion}
                        />
                      </Content>

                      <Footer
                        dispatch={dispatch}
                        showDisclaimer={props.showDisclaimer}
                        disclaimer={props.disclaimer}
                        percent={percent}
                        answer={state.answer}
                        {...currentQuestion}
                        handlePrevious={handlePrevious}
                        handleNoBudget={handleNoBudget}
                      />
                    </React.Fragment>
                  )}
                </FadeTransition>
              )}
            </Transition>
          </SwitchTransition>
        </Form>
      </Container>
    </QuizProvider>
  );
};

export default Questionnaire;

function getInitialState(props) {
  // Get first quiz, first question...
  const questions = get(props, "quizzes[0].questions");

  const firstQuestion = questions.find((question) => {
    let countries = get(question, "country");

    // No Countries selected, use this question
    if (!countries) return question;

    countries = pickBy(countries, (val) => val === true);

    // No specific countries, this is the first question
    if (size(countries) < 1) return question;

    // Country specific, check our country is allowed
    if (props.country in countries) return question;
  });

  return {
    quizzes: props.quizzes,
    answers: [],
    current: {
      quiz: props.quiz.id,
      question: firstQuestion.id,
    },
    answer: [],
    completed: false,
    country: props.country,
  };
}

function getRowCount(question) {
  let answers = question.answers.length;
  if (question.other) answers++;

  const mobileRows =
    question.columns > 2 && answers > 3
      ? Math.ceil(answers / question.columns)
      : answers;

  const rows = Math.ceil(answers / question.columns);

  return { mobileRows, rows };
}
